import { useMemo, useState } from 'react';
import { getWebApp } from './max.js';
import { lookupVin, type VinResult } from './api.js';

const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{11,17}$/i;

export function App() {
  const webApp = useMemo(() => getWebApp(), []);
  const [vin, setVin] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<VinResult | null>(null);
  const [hint, setHint] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = vin.trim().toUpperCase();

    if (!VIN_REGEX.test(value)) {
      setHint('VIN должен содержать 11–17 латинских букв и цифр (без I, O, Q).');
      setResult(null);
      return;
    }
    setHint(null);

    if (!webApp?.initData) {
      setResult({ ok: false, error: { code: 'NO_INIT_DATA', message: 'Откройте мини-приложение из бота MAX.' } });
      return;
    }

    setLoading(true);
    try {
      const res = await lookupVin(value, webApp.initData);
      setResult(res);
      webApp.HapticFeedback?.notificationOccurred(res.ok ? 'success' : 'error');
    } catch (err) {
      setResult({
        ok: false,
        error: { code: 'NETWORK', message: err instanceof Error ? err.message : 'Сетевая ошибка' },
      });
      webApp?.HapticFeedback?.notificationOccurred('error');
    } finally {
      setLoading(false);
    }
  };

  const user = webApp?.initDataUnsafe.user;

  return (
    <main className="page">
      <header className="page__header">
        <h1>Поиск по VIN</h1>
        {user ? (
          <p className="page__greeting">
            {user.first_name ?? user.username ?? 'Пользователь'}
          </p>
        ) : null}
      </header>

      <form className="form" onSubmit={onSubmit}>
        <label className="form__label" htmlFor="vin-input">
          VIN автомобиля
        </label>
        <input
          id="vin-input"
          className="form__input"
          type="text"
          inputMode="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          maxLength={17}
          value={vin}
          onChange={(e) => setVin(e.target.value.toUpperCase())}
          placeholder="XTA21703..."
          disabled={loading}
        />
        {hint ? <p className="form__hint">{hint}</p> : null}
        <button className="form__submit" type="submit" disabled={loading || vin.trim().length === 0}>
          {loading ? 'Ищем…' : 'Найти'}
        </button>
      </form>

      {result ? <ResultCard result={result} /> : null}
    </main>
  );
}

function ResultCard({ result }: { result: VinResult }) {
  if (!result.ok) {
    return (
      <section className="result result--error">
        <h2>Ошибка</h2>
        <p className="result__code">{result.error.code}</p>
        <p>{result.error.message}</p>
      </section>
    );
  }

  return (
    <section className="result result--ok">
      <h2>Найдено</h2>
      <dl className="result__grid">
        <dt>VIN</dt>
        <dd>{result.vin}</dd>
        {result.manager ? (
          <>
            <dt>Менеджер</dt>
            <dd>{result.manager.name ?? `Bitrix ID ${result.manager.bitrix_id}`}</dd>
          </>
        ) : null}
      </dl>

      {result.data === null || result.data === undefined ? null : typeof result.data === 'string' ? (
        <p className="result__data-string">{result.data}</p>
      ) : (
        <details className="result__details" open>
          <summary>Данные из 1С</summary>
          <pre>{JSON.stringify(result.data, null, 2)}</pre>
        </details>
      )}

      <p className="result__notice">{renderNotice(result.notified, result.manager !== null)}</p>
    </section>
  );
}

function renderNotice(notified: boolean | null, hasManager: boolean): string {
  if (!hasManager) return 'Менеджер в ответе 1С не указан — уведомление не отправлялось.';
  if (notified === true) return 'Менеджеру отправлено уведомление в Bitrix.';
  if (notified === false) return 'Уведомление менеджеру не отправлено — проверьте журнал на сервере.';
  return '';
}
